import { Badge, Box, createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { FunctionComponent } from 'react';
import { ProspectConversation } from 'model/ProspectMessage';
import clsx from 'clsx';
import moment from 'moment';
import { formatPhoneNumberByLength } from '../utils';

interface ConversationItemProps {
    prospectConversation: ProspectConversation;
    selected: boolean;
    onClick?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            'display': 'block',
            'cursor': 'pointer',
            'borderRadius': 10,
            'minHeight': '83px',
            'maxHeight': '83px',
            'margin': '10px',
            'padding': '12px 16px',
            'position': 'relative',
            'border': 'solid 1px transparent',
            'transition': 'background-color 0.1s ease-in, border-color 0.1s ease-in',
            '&:hover': {
                border: 'solid 1px #000',
                backgroundColor: '#F8F5F1'
            }
        },
        active: {
            border: 'solid 1px #000',
            backgroundColor: '#F8F5F1'
        },
        heading: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12
        },
        title: {
            color: '#222',
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 600,
            paddingRight: 6,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
        },
        titleUnread: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 600,
            paddingRight: 6,
            color: '#5E8677 !important',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
        },
        messageContainer: {
            display: '-webkit-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
            whiteSpace: 'normal'
        },
        message: {
            fontSize: 13,
            lineHeight: 1.2,
            fontWeigth: 400,
            color: '#7A7A7A',
            paddingRight: 25
        },
        messageUnread: {
            fontSize: 13,
            lineHeight: 1.2,
            fontWeight: 500,
            color: '#000',
            paddingRight: 25
        },
        sent: {
            fontSize: 11,
            lineHeight: 1,
            fontWeight: 500,
            color: '#A2A2A5',
            whiteSpace: 'nowrap'
        },
        sentUnread: {
            fontSize: 11,
            lineHeight: 1,
            fontWeight: 500,
            color: '#5E8677',
            whiteSpace: 'nowrap'
        },
        unReadBadgeRoot: {
            display: 'inline !important'
        },
        customBadge: {
            whiteSpace: 'nowrap',
            lineHeight: 1,
            backgroundColor: '#5E8677',
            color: 'white',
            borderRadius: '10px',
            top: '5px',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center,'
        }
    })
);

export const ConversationItem: FunctionComponent<ConversationItemProps> = props => {
    const classes = useStyles();

    return (
        <Box
            id={`chat-${props.prospectConversation.prospect.id}`}
            className={props.selected ? clsx(classes.root, classes.active) : classes.root}
            onClick={props.onClick}
        >
            <Box className={classes.heading}>
                <Typography
                    className={props.prospectConversation.unreadMessages > 0 ? classes.titleUnread : classes.title}
                >
                    {formatPhoneNumberByLength(props.prospectConversation.prospect.phone) ?? ''}
                </Typography>
                <span className={props.prospectConversation.unreadMessages > 0 ? classes.sentUnread : classes.sent}>
                    {moment(props.prospectConversation.lastMessage.sentAt).fromNow()}
                </span>
            </Box>
            <Grid
                container
                justifyContent="flex-start"
            >
                <Grid
                    item
                    xs={11}
                >
                    <Typography
                        className={
                            props.prospectConversation.unreadMessages > 0
                                ? clsx(classes.messageContainer, classes.messageUnread)
                                : clsx(classes.messageContainer, classes.message)
                        }
                    >
                        {props.prospectConversation.lastMessage.message}{' '}
                    </Typography>
                </Grid>

                <Grid
                    item
                    xs={1}
                >
                    {props.prospectConversation.unreadMessages > 0 && (
                        <Badge
                            classes={{
                                root: classes.unReadBadgeRoot,
                                badge: classes.customBadge
                            }}
                            badgeContent={props.prospectConversation.unreadMessages}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                        ></Badge>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default ConversationItem;
